import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_EMPLOYEE_WEEKLY_SCHEDULE } from '../../graphql/team-availability-queries';
import { SET_EMPLOYEE_WEEKLY_SCHEDULE } from '../../graphql/team-availability-mutations';
import { GET_USERS } from '../../graphql/queries';
import { toast } from 'react-hot-toast';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const buildBlankSchedule = () =>
  [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
    dayOfWeek,
    isWorking: null,
    isRemote: false,
  }));

function EditScheduleModal({ isOpen, onClose, userId, onSaved }) {
  const [schedule, setSchedule] = useState(buildBlankSchedule);

  const { data: userData } = useQuery(GET_USERS, {
    fetchPolicy: 'cache-first',
  });

  const { loading } = useQuery(GET_EMPLOYEE_WEEKLY_SCHEDULE, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const existing = data?.employeeWeeklySchedule;
      if (Array.isArray(existing) && existing.length > 0) {
        const merged = [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
          const match = existing.find((s) => s.dayOfWeek === dayOfWeek);
          if (!match) return { dayOfWeek, isWorking: false, isRemote: false };
          return {
            dayOfWeek,
            isWorking: match.isWorking,
            isRemote: match.isRemote === true,
          };
        });
        setSchedule(merged);
      } else {
        setSchedule(buildBlankSchedule());
      }
    },
  });

  const [setScheduleMutation, { loading: saving }] = useMutation(SET_EMPLOYEE_WEEKLY_SCHEDULE, {
    onCompleted: () => {
      toast.success('Schedule saved successfully');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save schedule');
    },
  });

  const user = userData?.users?.find((u) => u.id === userId);

  const handleToggleStatus = (dayOfWeek) => {
    setSchedule((prev) =>
      prev.map((entry) => {
        if (entry.dayOfWeek !== dayOfWeek) return entry;
        if (entry.isWorking === null) return { ...entry, isWorking: true, isRemote: false };
        if (entry.isWorking === true) return { ...entry, isWorking: false, isRemote: false };
        return { ...entry, isWorking: true, isRemote: false };
      })
    );
  };

  const handleLocationChange = (dayOfWeek, nextIsRemote) => {
    setSchedule((prev) =>
      prev.map((entry) => {
        if (entry.dayOfWeek !== dayOfWeek) return entry;
        if (entry.isWorking !== true) return entry;
        if (entry.isRemote === nextIsRemote) return entry;
        return { ...entry, isRemote: nextIsRemote };
      })
    );
  };

  const handleSave = () => {
    const explicit = schedule.filter((day) => day.isWorking !== null);
    if (explicit.length === 0) {
      toast.error('Please set at least one day');
      return;
    }
    const allDays = [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
      const match = explicit.find((d) => d.dayOfWeek === dayOfWeek);
      const isWorking = match ? match.isWorking === true : false;
      return {
        dayOfWeek,
        isWorking,
        isRemote: isWorking && match?.isRemote === true,
      };
    });
    setScheduleMutation({
      variables: {
        input: {
          userId,
          days: allDays,
        },
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Edit Schedule - {user?.name || 'Employee'}
            </h3>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Click each day to toggle working/off. Choose On-Island or Remote for working days.
              </p>
              <div className="space-y-2 mb-6">
                {schedule.map((day) => {
                  const isChecked = day.isWorking === true;
                  const isOff = day.isWorking === false;
                  const isNotSet = day.isWorking === null;
                  const stateClass = isChecked
                    ? 'border-sky-200 bg-sky-50'
                    : isOff
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-dashed border-gray-300 bg-white';
                  return (
                    <div
                      key={day.dayOfWeek}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border rounded-lg ${stateClass}`}
                    >
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(day.dayOfWeek)}
                        aria-pressed={isChecked}
                        className="flex items-center gap-3 flex-1 text-left"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          readOnly
                          ref={(element) => {
                            if (element) element.indeterminate = isNotSet;
                          }}
                          className="w-5 h-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500 pointer-events-none"
                          tabIndex={-1}
                        />
                        <span className="text-sm font-medium text-gray-900 w-24">
                          {DAY_NAMES[day.dayOfWeek]}
                        </span>
                        {isNotSet && (
                          <span className="text-xs text-gray-400 italic">(not set)</span>
                        )}
                        {isOff && <span className="text-xs text-gray-500">(off)</span>}
                      </button>

                      {isChecked && (
                        <div
                          className="inline-flex p-0.5 rounded-md bg-white border border-gray-200 self-start sm:self-auto"
                          role="group"
                          aria-label={`${DAY_NAMES[day.dayOfWeek]} location`}
                        >
                          <button
                            type="button"
                            onClick={() => handleLocationChange(day.dayOfWeek, false)}
                            aria-pressed={day.isRemote === false}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                              day.isRemote === false
                                ? 'bg-sky-600 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            On-Island
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLocationChange(day.dayOfWeek, true)}
                            aria-pressed={day.isRemote === true}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                              day.isRemote === true
                                ? 'bg-purple-600 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Remote
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Schedule'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditScheduleModal;
